import { get } from 'svelte/store';
import { Sentry } from '$lib/services/sentry';
import createPersistentStore from '$lib/utils/persistent-store';
import { AOFL_PRODUCT_NAMES, LINK_FROM_APP_PLATFORMS } from '$lib/utils/constants';

/** @type {import('./types').App.AppStore['global']} */
export const DEFAULT_APP_GLOBAL_STATE = {
	analyticsCookiesEnabled: true,
	appProduct: AOFL_PRODUCT_NAMES.ABCMOUSE_2_0,
	desktopChoice: '',
	featureFlags: {},
	gdprVisible: false,
	isFirstTimeVisit: true,
	isLinkFromApp: false, // initialized by inbound-gateway payload
	isLinkFromAppPlatform: null, // or string, initialized by inbound-gateway payload
	isLinkFromEmail: 'false', // string for use in experiment audience evaluation
	linkFromAppPlatform: LINK_FROM_APP_PLATFORMS.DEFAULT, // from query string or inbound-gateway payload
	loading: false,
	targetingCookiesEnabled: true,
};

/**
 * Localization meta captured once per session from the entry URL
 * (`?ff_l10n=`/`?locale=`) plus the saved display-locale seam. These live in
 * the store for the session only — never in localStorage — so a new browser
 * session starts from these defaults unless the entry URL sets them again.
 * @type {Pick<import('./types').App.Meta, 'l10nEnabled' | 'localeOverride' | 'savedDisplayLocale'>}
 */
export const SESSION_ONLY_META_DEFAULTS = {
	l10nEnabled: false,
	localeOverride: null,
	savedDisplayLocale: null,
};

/**
 * Resets the session-only localization meta on the copy handed to localStorage
 * and drops any experiment assignments an older build left there.
 * @param {import('./types').App.AppStore} state - Live app store state.
 * @returns {import('./types').App.AppStore} State safe to serialize.
 */
const toPersistedAppState = (state) => {
	const global = /** @type {import('./types').App.Global & { experiments?: unknown }} */ ({
		...state?.global,
	});
	delete global.experiments;
	return {
		...state,
		global,
		meta: {
			...state?.meta,
			...SESSION_ONLY_META_DEFAULTS,
		},
	};
};

/**
 * @type {import('svelte/store').Writable<import('./types').App.AppStore>} AppStore
 */
export const app = createPersistentStore(
	'meta-app-store',
	{
		meta: {
			acquisitionSource: '',
			appStore: '',
			appVersion: null,
			deviceId: null,
			environment: 'development',
			inApp: false, // maps to is_webview
			isAmazonApp: false,
			isAmazonViewer: false,
			isAndroidApp: false,
			isAndroidViewer: '',
			isDesktopViewer: '',
			isIosApp: false,
			isIosViewer: '',
			isMac: false,
			isMobileViewer: '',
			isOnnDevice: false,
			isSamsungApp: false,
			isSmartTvApp: false,
			isSmarttvViewer: '',
			isTabletViewer: '',
			isWindows: false,
			localeCode: '',
			viewerCountry: 'US',
			viewerCountryRegion: '',
			viewerPostalCode: '',
			...SESSION_ONLY_META_DEFAULTS,
		},
		global: DEFAULT_APP_GLOBAL_STATE,
	},
	{ sanitize: toPersistedAppState }
);

/**
 * @param {{ [key: string]: string | boolean | null; }} metaValues - new meta values
 * @returns {Promise<void>}
 */
export const setMetaStore = async (metaValues) => {
	app.update((current) => ({
		...current,
		meta: {
			...current.meta,
			...metaValues,
		},
	}));

	Object.keys(get(app).meta).forEach((key) => {
		Sentry.setTag(key, get(app)?.meta?.[key]);
	});

	Sentry.setContext('meta', get(app).meta);
	// Awaiting till next tick to ensure localStorage writes are complete before window navigate
	await new Promise((resolve) => setTimeout(resolve, 0));
};

/**
 * @param {{ [key: string]: string | boolean | null }} globalValues - new global values
 * @returns {Promise<void>}
 */
export const setGlobalStore = async (globalValues) => {
	app.set({ ...get(app), global: { ...get(app)?.global, ...globalValues } });

	Sentry.setTag('appProduct', get(app)?.global?.appProduct);
	Sentry.setTag('linkFromAppPlatform', get(app)?.global?.linkFromAppPlatform);
	Sentry.setTag('isFirstTimeVisit', get(app)?.global?.isFirstTimeVisit);
	Sentry.setContext('global', get(app)?.global);
	// Awaiting till next tick to ensure localStorage writes are complete before window navigate
	await new Promise((resolve) => setTimeout(resolve, 0));
};

/**
 * Resets app.global to its default session state. Called on logout.
 * @returns {Promise<void>}
 */
export const resetGlobalStore = async () => {
	await setGlobalStore({ ...DEFAULT_APP_GLOBAL_STATE });
};
